import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useSocket } from '../../contexts/SocketContext';
import Modal, { useConfirm } from '../../components/Modal';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import TabBar from '../../components/TabBar';
import BizSwitch from '../../components/BizSwitch';
import SubTabs from '../../components/SubTabs';
import { setBizMode } from '../../utils/bizMode';

import api from '../../utils/api';
import {
  ArrowLeft, Download, Users, ImageIcon,
  ChevronDown, ChevronRight, MailX, Loader, AlertTriangle, X, Edit3, Plus, CheckCircle2,
  Check, RotateCcw, AlertCircle, XCircle
} from 'lucide-react';
import OcrAdminDetail from '../../components/OcrAdminDetail';
import { OriginModal, dupBadge } from '../../components/DuplicateOrigin';

// ─── 图片灯箱 ────────────────────────────────────────────────────────────
function Lightbox({ images, startIndex, onClose }) {
  // 防御：images 为 null/undefined/非数组时安全降级为空列表
  const list = Array.isArray(images) ? images : [];
  const safeStart = Math.min(Math.max(startIndex || 0, 0), Math.max(list.length - 1, 0));
  const [idx, setIdx] = useState(safeStart);
  const total = list.length;

  useEffect(() => {
    // 空列表直接关闭，避免渲染空 DOM
    if (total === 0) { onClose(); return; }
    const handler = (e) => {
      if (e.key === 'ArrowLeft') setIdx(i => (i - 1 + total) % total);
      if (e.key === 'ArrowRight') setIdx(i => (i + 1) % total);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [total, onClose]);

  if (total === 0) return null;

  // portal 到 body 并使用 32-bit 顶级 z-index，确保盖在所有抽屉/弹层之上
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483600,
        background: 'rgba(0,0,0,0.92)', display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 'calc(16px + env(safe-area-inset-top, 0px))', right: 'calc(16px + env(safe-area-inset-right, 0px))',
          background: 'rgba(255,255,255,0.1)', border: 'none',
          borderRadius: '50%', width: 36, height: 36,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
      >
        <X size={18} color="white" />
      </button>

      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
        {idx + 1} / {total}
      </div>

      <img
        src={list[idx].url}
        alt=""
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '90vw', maxHeight: '75vh',
          objectFit: 'contain', borderRadius: 12
        }}
      />

      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 10 }}>
        {list[idx].originalName || ''}
      </div>

      {total > 1 && (
        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          <button
            onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + total) % total); }}
            style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
              padding: '8px 20px', color: 'white', cursor: 'pointer', fontSize: 14
            }}
          >← 上一张</button>
          <button
            onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % total); }}
            style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
              padding: '8px 20px', color: 'white', cursor: 'pointer', fontSize: 14
            }}
          >下一张 →</button>
        </div>
      )}
    </div>,
    document.body
  );
}

// ─── 单用户图片页 (/admin/images/:userId) ───────────────────────────────
// 支持：查看 / 导出 / 补充图片 / 纠错（删除、替换、补充新图）——
// 与前台工作台一致的能力，代完成上传的图片也能在这里补充、修正
function UserImageView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { subscribe } = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [lightbox, setLightbox] = useState(null); // {images, idx}
  const [dupInfo, setDupInfo] = useState(null);      // 点击"重复"角标后要展示来源的那张图
  const [exporting, setExporting] = useState(false);
  const [exportDownloaded, setExportDownloaded] = useState(0); // bytes
  const [exportTotal, setExportTotal] = useState(0);           // bytes (0=unknown)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // 导出完成后："立即清理" or "暂不清理" 选择弹窗；{ exportId, count }
  const [cleanupPrompt, setCleanupPrompt] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [showOcrDetail, setShowOcrDetail] = useState(false); // OCR Tabs 展开
  // 补充/纠错状态
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const uploadTargetRef = useRef(null); // 当前补充的批次 {batchId, batchLabel}（用 ref 避免 setState 批处理延迟导致回调读到旧值）

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/images/user/${userId}`);
      setData(res.data);
      // 默认展开第一个日期
      if (res.data.groups?.length > 0) {
        setExpanded({ [res.data.groups[0].date]: true });
      }
    } catch (err) {
      showToast(err.response?.data?.error || '加载失败', 'error');
    } finally { setLoading(false); }
  }, [userId, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 实时刷新：OCR 完成、代上传、stats_refresh 均触发重拉图片列表
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'ocr_progress') {
        clearTimeout(window._imgMgmtOcrTimer);
        window._imgMgmtOcrTimer = setTimeout(() => fetchData(), 800);
      } else if (['ocr_batch_done', 'image_stats_refresh'].includes(event)) {
        setTimeout(() => fetchData(), 400);
      } else if (event === 'stats_refresh') {
        clearTimeout(window._imgMgmtStatsTimer);
        window._imgMgmtStatsTimer = setTimeout(() => fetchData(), 1200);
      }
    });
  }, [subscribe, fetchData]);

  // ── 补充图片：弹文件选择 → 上传到该批次（归属目标用户）──
  const handleSupplementPick = (batch) => {
    uploadTargetRef.current = { batchId: batch.batchId, batchLabel: batch.batchLabel };
    fileRef.current?.click();
  };
  const handleSupplementUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // 清空 value，保证同一批次可以再次选同一文件
    const target = uploadTargetRef.current; // 立即读取，避免异步后 ref 被覆盖
    if (!files.length || !target) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('images', f);
      await api.post(`/images/upload/${target.batchId}?targetUserId=${userId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(`已补充 ${files.length} 张图片`, 'success');
      uploadTargetRef.current = null;
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || '补充失败', 'error');
    } finally { setUploading(false); }
  };

  // ── 纠错：删除单张图片 ──
  const handleDeleteImg = async (img) => {
    const ok = await confirm(`确认删除「${img.originalName || '这张图片'}」？`, { detail: '删除后不可恢复。', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/images/${img.id}`);
      showToast('已删除', 'success');
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    }
  };

  // ── 纠错：替换单张图片 ──
  const handleReplaceImg = async (img) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    document.body.appendChild(input); // 必须挂载到 DOM，iOS Safari / 微信内置浏览器才能弹出文件选择

    const cleanup = () => {
      if (document.body.contains(input)) document.body.removeChild(input);
    };

    input.onchange = async (ev) => {
      const file = ev.target.files?.[0];
      cleanup();
      if (!file) return;
      if (file.size > 15 * 1024 * 1024) { showToast('图片不能超过 15MB', 'error'); return; }
      try {
        const fd = new FormData();
        fd.append('image', file);
        await api.post(`/images/${img.id}/replace`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        showToast('替换成功', 'success');
        fetchData();
      } catch (err) {
        showToast(err.response?.data?.error || '替换失败', 'error');
      }
    };

    // 用户取消选择（不触发 onchange）时，在页面重新获得焦点后兜底清理
    window.addEventListener('focus', function onFocus() {
      window.removeEventListener('focus', onFocus);
      setTimeout(cleanup, 500);
    }, { once: true });

    input.click();
  };

  const handleExport = () => {
    setExporting(true);
    setExportDownloaded(0);
    setExportTotal(0);
    const token = localStorage.getItem('token');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/images/export', true);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.timeout = 900000;

    xhr.onprogress = (e) => {
      setExportDownloaded(e.loaded);
      if (e.lengthComputable && e.total > 0) setExportTotal(e.total);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const blob = xhr.response;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const cd = xhr.getResponseHeader('content-disposition') || '';
        const match = cd.match(/filename\*?=(?:UTF-8'')?([^;]+)/i);
        a.download = match ? decodeURIComponent(match[1]) : `images_${userId}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        const exportId = xhr.getResponseHeader('X-Image-Export-Id');
        showToast('导出成功，ZIP 已下载', 'success');
        setShowDeleteConfirm(false);
        const exportedCount = totalImages;
        fetchData();
        if (exportId) setCleanupPrompt({ exportId, count: exportedCount });
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          try { showToast(JSON.parse(reader.result).error || '导出失败', 'error'); }
          catch { showToast('导出失败', 'error'); }
        };
        reader.readAsText(xhr.response);
      }
      setExporting(false);
    };
    xhr.onerror = () => { showToast('网络错误，导出失败', 'error'); setExporting(false); };
    xhr.ontimeout = () => { showToast('请求超时，导出失败', 'error'); setExporting(false); };
    xhr.send(JSON.stringify({ userIds: userId }));
  };

  const totalImages = data?.groups?.reduce((s, g) => s + g.totalImages, 0) || 0;
  // 所有批次 OCR 汇总
  const allOcr = (data?.groups || []).flatMap(g => g.batches || []).reduce(
    (acc, b) => {
      const o = b.ocr || {};
      acc.qualified += o.qualified || 0;
      acc.duplicate += o.duplicate || 0;
      acc.invalid   += o.invalid   || 0;
      acc.failed    += o.failed    || 0;
      acc.total     += o.total     || 0;
      if (o.doneAt && !acc.doneAt) acc.doneAt = o.doneAt;
      return acc;
    },
    { qualified: 0, duplicate: 0, invalid: 0, failed: 0, total: 0, doneAt: null }
  );
  const ocrDone = allOcr.doneAt && allOcr.total > 0;
  const ocrRunning = !allOcr.doneAt && allOcr.total > 0;

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, fontWeight: 700, fontSize: 16 }}>
          {data?.user?.name || data?.user?.username || (String(data?.user?.phone||'').startsWith('ext') ? '' : data?.user?.phone) || '用户图片'}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <AdminNotificationBell />
          {totalImages > 0 && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={exporting}
              style={{ minWidth: 80 }}
            >
              {exporting ? (
                exportTotal > 0
                  ? `已下载 ${Math.round(exportDownloaded / exportTotal * 100)}%`
                  : exportDownloaded > 0
                  ? `${(exportDownloaded / 1024 / 1024).toFixed(1)}MB`
                  : <><Loader size={13} /> 打包中...</>
              ) : <><Download size={13} /> 导出</>}
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* OCR 汇总卡片 */}
        {!loading && data?.groups?.length > 0 && allOcr.total > 0 && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {ocrRunning && <span style={{ fontSize: 11, color: 'var(--c-primary)', display: 'flex', alignItems: 'center', gap: 3 }}><Loader size={10} className="spin" />识别中…</span>}
                  {ocrDone && <span style={{ fontSize: 11, color: 'var(--c-success)', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Check size={10} strokeWidth={2.5} /> 识别完成</span>}
                  {allOcr.qualified > 0 && <span style={{ fontSize: 11, background: 'rgba(52,199,89,.1)', color: 'var(--c-success)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>成功 {allOcr.qualified} 组</span>}
                  {allOcr.duplicate > 0 && <span style={{ fontSize: 11, background: 'rgba(255,149,0,.1)', color: 'var(--c-accent)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>重复 {allOcr.duplicate}</span>}
                  {allOcr.invalid > 0 && <span style={{ fontSize: 11, background: 'rgba(255,59,48,.08)', color: 'var(--c-danger)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>无效 {allOcr.invalid}</span>}
                  {allOcr.failed > 0 && <span style={{ fontSize: 11, background: 'var(--c-bg)', color: 'var(--c-text-secondary)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>失败 {allOcr.failed}</span>}
                </div>
              </div>
              <button className="btn btn-outline btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                onClick={() => setShowOcrDetail(v => !v)}>
                {showOcrDetail ? '收起' : '查看OCR'}
                <ChevronDown size={13} style={{ transform: showOcrDetail ? 'rotate(180deg)' : 'none', transition: '.15s' }} />
              </button>
            </div>
            {showOcrDetail && (
              <div style={{ marginTop: 14 }}>
                <OcrAdminDetail userId={userId} onDataChange={fetchData} />
              </div>
            )}
          </div>
        )}

        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : !data?.groups?.length ? (
          <div className="card empty-state">
            <MailX size={40} color="var(--c-text-secondary)" strokeWidth={1.2} style={{ margin: '0 auto 12px', display: 'block' }} />
            <p>该用户暂无上传图片</p>
          </div>
        ) : (
          data.groups.map(group => (
            <div key={group.date} className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 10 }}>
              {/* 日期行 */}
              <div
                onClick={() => setExpanded(p => ({ ...p, [group.date]: !p[group.date] }))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', cursor: 'pointer',
                  background: 'var(--c-bg)'
                }}
              >
                {expanded[group.date]
                  ? <ChevronDown size={16} color="var(--c-text-secondary)" />
                  : <ChevronRight size={16} color="var(--c-text-secondary)" />}
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{group.date}</span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--c-text-secondary)' }}>
                    共 {group.batches.length} 批 · {group.totalImages} 张
                  </span>
                </div>
              </div>

              {/* 批次列表 */}
              {expanded[group.date] && group.batches.map((batch, bi) => {
                // 所有图片展平，供灯箱用（防御：后端异常/旧缓存下 images 可能为 null）
                const allImgs = group.batches.flatMap(b => b?.images || []);
                const batchStartIdx = group.batches.slice(0, bi)
                  .reduce((s, b) => s + (b?.images?.length || 0), 0);
                const batchImages = batch?.images || [];

                return (
                  <div key={batch.batchId} style={{ padding: '10px 14px', borderTop: '0.5px solid var(--c-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)' }}>
                        {batch.batchLabel} · {batch.quantity}条 · {batchImages.length}张图片
                        <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginLeft: 6, fontWeight: 400 }}>
                          {batch.createdAt?.slice(11, 16)}
                        </span>
                      </span>
                      <span style={{ flex: 1 }} />
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => handleSupplementPick(batch)}
                        style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 3 }}
                      >
                        <Plus size={12} /> 补充
                      </button>
                    </div>
                    {/* OCR 汇总标签行 */}
                    {batch.ocr && batch.ocr.total > 0 && (
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {batch.ocr.doneAt ? (
                          <span style={{ fontSize: 10, color: 'var(--c-text-tertiary)' }}>识别完成</span>
                        ) : (
                          <span style={{ fontSize: 10, color: 'var(--c-primary)' }}>识别中…</span>
                        )}
                        {batch.ocr.qualified > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-success)', background: 'rgba(52,199,89,0.1)', borderRadius: 6, padding: '1px 7px' }}>
                            <Check size={10} strokeWidth={2.5} style={{marginRight:3,verticalAlign:'-1px'}} />成功 {batch.ocr.qualified} 组
                          </span>
                        )}
                        {batch.ocr.duplicate > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-accent)', background: 'rgba(255,149,0,0.1)', borderRadius: 6, padding: '1px 7px' }}>
                            <RotateCcw size={10} strokeWidth={2.5} style={{marginRight:3,verticalAlign:'-1px'}} />重复 {batch.ocr.duplicate} 组
                          </span>
                        )}
                        {batch.ocr.invalid > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-danger)', background: 'rgba(255,59,48,0.1)', borderRadius: 6, padding: '1px 7px' }}>
                            <AlertCircle size={10} strokeWidth={2.5} style={{marginRight:3,verticalAlign:'-1px'}} />无效 {batch.ocr.invalid} 张
                          </span>
                        )}
                        {batch.ocr.failed > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-text-secondary)', background: 'var(--c-bg)', borderRadius: 6, padding: '1px 7px' }}>
                            <XCircle size={10} strokeWidth={2.5} style={{marginRight:3,verticalAlign:'-1px'}} />失败 {batch.ocr.failed} 张
                          </span>
                        )}
                      </div>
                    )}

                    {batchImages.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', padding: '8px 0' }}>
                        未上传图片（可点右上角「补充」添加）
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                        {batchImages.map((img, imgIdx) => (
                          <div key={img.id} style={{ position: 'relative' }}>
                            <img
                              src={img.thumbUrl || img.url}
                              alt=""
                              loading="lazy"
                              onClick={() => setLightbox({ images: allImgs, idx: batchStartIdx + imgIdx })}
                              style={{
                                width: '100%', aspectRatio: '1',
                                objectFit: 'cover', borderRadius: 8,
                                border: '1px solid var(--c-border)', cursor: 'zoom-in', display: 'block'
                              }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                            {/* OCR 结果角标（左下角）。"重复"角标可点击查看来源：跨人撞号红色"重复·他人"，
                                自己传重橙色"重复"，点开显示是谁、哪一批、首次成功的那张图 */}
                            {img.ocrResult && (() => {
                              const isDup = img.ocrResult === 'duplicate';
                              const dup = isDup ? dupBadge(img.originBatch) : null;
                              return (
                                <div
                                  {...(isDup ? {
                                    role: 'button', title: dup.title,
                                    onClick: (e) => { e.stopPropagation(); setDupInfo(img); },
                                  } : {})}
                                  style={{
                                    position: 'absolute', bottom: 4, left: 4,
                                    borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 700,
                                    cursor: isDup ? 'pointer' : 'default',
                                    background: img.ocrResult === 'qualified' ? 'rgba(52,199,89,0.85)'
                                              : img.ocrResult === 'manual_set' ? 'rgba(88,86,214,0.85)'
                                              : isDup ? dup.background
                                              : 'rgba(255,59,48,0.85)',
                                    color: '#fff',
                                  }}>
                                  {img.ocrResult === 'qualified'  ? (img.ocrPhone || '已匹配')
                                   : img.ocrResult === 'manual_set' ? '手动'
                                   : isDup ? dup.text
                                   : (img.ocrResult || '').startsWith('invalid') ? '无效'
                                   : '失败'}
                                </div>
                              );
                            })()}
                          {/* 纠错按钮：替换 / 删除 */}
                            <div style={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 4 }}>
                              <button
                                title="替换此图片"
                                onClick={(e) => { e.stopPropagation(); handleReplaceImg(img); }}
                                style={{
                                  width: 22, height: 22, borderRadius: 6, padding: 0,
                                  background: 'rgba(0,122,255,0.9)', border: '1px solid #fff',
                                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                              >
                                <Edit3 size={11} color="#fff" />
                              </button>
                              <button
                                title="删除此图片"
                                onClick={(e) => { e.stopPropagation(); handleDeleteImg(img); }}
                                style={{
                                  width: 22, height: 22, borderRadius: 6, padding: 0,
                                  background: 'rgba(255,59,48,0.9)', border: '1px solid #fff',
                                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                              >
                                <X size={11} color="#fff" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* 隐藏的文件选择：补充图片用（归属该用户） */}
      <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
        onChange={handleSupplementUpload} />
      {uploading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 15, fontWeight: 600
        }}>
          <Loader size={20} className="spin" style={{ marginRight: 8 }} /> 上传中...
        </div>
      )}

      {/* 导出确认弹窗 */}
      <Modal show={showDeleteConfirm} onClose={() => !exporting && setShowDeleteConfirm(false)} title="导出图片">
        <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
          <AlertTriangle size={44} color="var(--c-accent)" style={{ margin: '0 auto 12px', display: 'block' }} />
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
            导出 {data?.user?.name || '该用户'} 的全部 {totalImages} 张图片？
          </div>
          <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 20, lineHeight: 1.7 }}>
            图片将打包为 ZIP 下载，并按<br />
            <strong>第N批 时间 / 成员 张数</strong> 分文件夹整理，单个文件夹超过200张会自动再分组。<br />
            下载后仅标记为"已打包"，不会立即删除，确认完成后可选择立即清理或稍后清理。
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-outline" style={{ flex: 1, height: 46 }}
              onClick={() => setShowDeleteConfirm(false)} disabled={exporting}>取消</button>
            <button className="btn btn-primary" style={{ flex: 1, height: 46 }}
              onClick={handleExport} disabled={exporting}>
              {exporting
                ? exportTotal > 0
                  ? `打包中...已下载 ${Math.round(exportDownloaded / exportTotal * 100)}%`
                  : exportDownloaded > 0
                  ? `打包中...${(exportDownloaded / 1024 / 1024).toFixed(1)}MB`
                  : <><Loader size={14} /> 打包中...</>
                : <><Download size={14} /> 确认导出</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* 灯箱 */}
      {dupInfo && (
        <OriginModal
          img={dupInfo}
          audience="admin"
          onClose={() => setDupInfo(null)}
          onOpenImage={(im) => setLightbox({ images: [{ url: im.url, originalName: '首次成功的图片' }], idx: 0 })}
          onGoUser={(uid) => { setDupInfo(null); navigate(`/admin/images/${uid}`); }}
        />
      )}
      {lightbox && Array.isArray(lightbox.images) && lightbox.images.length > 0 && (
        <Lightbox
          images={lightbox.images}
          startIndex={lightbox.idx}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* 导出后：立即清理 / 暂不清理 */}
      <CleanupPromptModal
        prompt={cleanupPrompt}
        cleaning={cleaningUp}
        onClose={() => setCleanupPrompt(null)}
        onCleanup={async () => {
          if (!cleanupPrompt) return;
          setCleaningUp(true);
          try {
            await api.post(`/images/export/${cleanupPrompt.exportId}/cleanup`);
            showToast('已清理服务器上的已导出图片', 'success');
            fetchData();
          } catch (err) {
            showToast(err.response?.data?.error || '清理失败', 'error');
          } finally {
            setCleaningUp(false);
            setCleanupPrompt(null);
          }
        }}
      />

      <TabBar mode="admin" />
    </div>
  );
}

// ─── 导出完成后："立即清理" or "暂不清理" 选择弹窗 ─────────────────────────
// 导出只会给图片打"已打包"标记，不会立即删除文件；选"暂不清理"也没关系，
// 之后随时可以在"图片管理"汇总页点击常驻的"清理已导出图片"按钮再删除。
function CleanupPromptModal({ prompt, cleaning, onClose, onCleanup }) {
  return (
    <Modal show={!!prompt} onClose={cleaning ? () => {} : onClose} title="导出完成">
      {prompt && (
        <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
          <CheckCircle2 size={44} color="var(--c-success)" style={{ margin: '0 auto 12px', display: 'block' }} />
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
            已导出 {prompt.count} 张图片，现在清理服务器上的这些图片？
          </div>
          <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 20, lineHeight: 1.7 }}>
            选择"暂不清理"也没关系，之后可以随时在图片管理页点击<br />
            <strong>"清理已导出图片"</strong> 按钮再删除。
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-outline" style={{ flex: 1, height: 46 }} onClick={onClose} disabled={cleaning}>暂不清理</button>
            <button className="btn btn-primary" style={{ flex: 1, height: 46 }} onClick={onCleanup} disabled={cleaning}>
              {cleaning ? '清理中...' : '立即清理'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── 所有用户图片汇总页 (/admin/images) ─────────────────────────────────
function ImageSummaryPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { subscribe } = useSocket();
  const [users, setUsers] = useState([]);
// eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportDownloaded, setExportDownloaded] = useState(0); // bytes
  const [exportTotal, setExportTotal] = useState(0);           // bytes (0=unknown)
  const [showExportAll, setShowExportAll] = useState(false);
  // 导出后："立即清理" or "暂不清理" 选择弹窗；{ exportId, count }
  const [cleanupPrompt, setCleanupPrompt] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  // 尚未清理的历史导出（常驻展示，"暂不清理"选择后仍能随时回来清理）
  const [pendingExports, setPendingExports] = useState({ count: 0, totalImages: 0, exports: [] });
  // 成功/重复/无效/失败 实时汇总（含已导出/待导出拆分），供顶部汇总卡 + 批量按钮使用
  const [ocrOverview, setOcrOverview] = useState(null);
  const [bulkRematchLoading, setBulkRematchLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/images/summary');
      setUsers(res.data.users || []);
    } catch (err) {
      showToast(err.response?.data?.error || '加载失败', 'error');
    } finally { setLoading(false); }
  }, [showToast]);

  const fetchPendingExports = useCallback(() => {
    api.get('/images/exports/pending').then(res => setPendingExports(res.data)).catch(() => {});
  }, []);

  const fetchOcrOverview = useCallback(() => {
    api.get('/images/ocr-overview').then(res => setOcrOverview(res.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchSummary(); fetchPendingExports(); fetchOcrOverview(); }, [fetchSummary, fetchPendingExports, fetchOcrOverview]);

  // 实时刷新：收到图片上传/更新/OCR完成事件立即拉最新汇总，避免手动刷新
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (['stats_refresh', 'users_updated', 'ocr_batch_done', 'image_stats_refresh'].includes(event)) {
        fetchSummary();
        fetchOcrOverview();
      }
    });
  }, [subscribe, fetchSummary, fetchOcrOverview]);

  // 批量重新匹配：全体范围内所有"无效"（不在库/已过期）的图重新对照当前数据池判定一次
  const handleBulkRematchAll = async () => {
    setBulkRematchLoading(true);
    try {
      const res = await api.post('/images/rematch-bulk', {});
      showToast(res.data.message || '重新匹配完成', res.data.rematched > 0 ? 'success' : 'info');
      fetchOcrOverview();
      fetchSummary();
    } catch (err) {
      showToast(err.response?.data?.error || '重新匹配失败', 'error');
    } finally {
      setBulkRematchLoading(false);
    }
  };

  // 清理全部待清理的导出（可能不止一批）
  const handleCleanupAllPending = async () => {
    if (!pendingExports.exports?.length) return;
    setCleaningUp(true);
    try {
      for (const e of pendingExports.exports) {
        await api.post(`/images/export/${e.export_id}/cleanup`);
      }
      showToast('已清理服务器上的已导出图片', 'success');
      fetchSummary();
      fetchPendingExports();
      fetchOcrOverview();
    } catch (err) {
      showToast(err.response?.data?.error || '清理失败', 'error');
      fetchPendingExports();
    } finally {
      setCleaningUp(false);
    }
  };

  const handleExportAll = () => {
    setExporting(true);
    setExportDownloaded(0);
    setExportTotal(0);
    const token = localStorage.getItem('token');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/images/export', true);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.timeout = 900000;

    xhr.onprogress = (e) => {
      setExportDownloaded(e.loaded);
      if (e.lengthComputable && e.total > 0) setExportTotal(e.total);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const blob = xhr.response;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const cd = xhr.getResponseHeader('content-disposition') || '';
        const match = cd.match(/filename\*?=(?:UTF-8'')?([^;]+)/i);
        a.download = match ? decodeURIComponent(match[1]) : 'images_all.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        const exportId = xhr.getResponseHeader('X-Image-Export-Id');
        showToast('全体导出成功，ZIP 已下载', 'success');
        const exportedCount = grandTotal;
        setShowExportAll(false);
        fetchSummary();
        fetchPendingExports();
        fetchOcrOverview();
        if (exportId) setCleanupPrompt({ exportId, count: exportedCount });
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          try { showToast(JSON.parse(reader.result).error || '导出失败', 'error'); }
          catch { showToast('导出失败', 'error'); }
        };
        reader.readAsText(xhr.response);
      }
      setExporting(false);
    };
    xhr.onerror = () => { showToast('网络错误，导出失败', 'error'); setExporting(false); };
    xhr.ontimeout = () => { showToast('请求超时，导出失败', 'error'); setExporting(false); };
    xhr.send(JSON.stringify({ userIds: 'all' }));
  };

  const totalWithImages = users.filter(u => u.image_count > 0).length;
  const grandTotal = users.reduce((s, u) => s + (u.image_count || 0), 0);
  const totalPackaged = users.reduce((s, u) => s + (u.packaged_count || 0), 0);
  // 列表展示仍有图片或今日上传过图片的用户，导出清空后今日统计仍保留
  const usersWithImages = users.filter(u => u.image_count > 0 || (u.today_image_count || 0) > 0 || (u.packaged_count || 0) > 0);
  // 顶部汇总卡最后一格：成功/重复/无效/失败 四类合计的 已导出/待导出（不按类别拆分）
  const ocrExportedTotal = ocrOverview
    ? ocrOverview.qualified.exported + ocrOverview.duplicate.exported + ocrOverview.invalid.exported + ocrOverview.failed.exported
    : 0;
  const ocrPendingTotal = ocrOverview
    ? ocrOverview.qualified.pending + ocrOverview.duplicate.pending + ocrOverview.invalid.pending + ocrOverview.failed.pending
    : 0;

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate('/admin')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, fontWeight: 700, fontSize: 16 }}>图片管理</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <AdminNotificationBell />
          {(grandTotal > 0 || totalPackaged > 0) && (
            <button className="btn btn-primary btn-sm" onClick={() => { fetchSummary(); setShowExportAll(true); }} disabled={exporting} style={{ minWidth: 90 }}>
              {exporting
                ? exportTotal > 0
                  ? `已下载 ${Math.round(exportDownloaded / exportTotal * 100)}%`
                  : exportDownloaded > 0
                  ? `${(exportDownloaded / 1024 / 1024).toFixed(1)}MB`
                  : <><Loader size={13} /> 打包中...</>
                : <><Download size={13} /> 导出全体</>}
            </button>
          )}
        </div>
      </div>

      {/* 排五 / 证券 切换 + 识别记录 | 图片管理 标签（在证券侧切回来会落在对应的「图片管理」标签） */}
      <div style={{ padding: '10px 16px 0' }}>
        <BizSwitch value="legacy" onChange={(k) => { if (k === 'puzzle') { setBizMode('puzzle'); navigate('/admin/puzzle-records?tab=images', { replace: true }); } }} />
      </div>
      <div style={{ padding: '8px 16px 0' }}>
        <SubTabs items={[['records', '识别记录'], ['images', '图片管理']]} value="images"
          onChange={(k) => { if (k === 'records') navigate('/admin/ocr', { replace: true }); }} />
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* 汇总卡：成功/重复/无效/失败 四个总数 + 一个单独的"待导出"合计格；已导出放右上角小字，不参与叠放避免错位 */}
        <div className="card" style={{ display: 'flex', gap: 0, alignItems: 'stretch', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 10, right: 14, fontSize: 11, color: 'var(--c-text-tertiary)' }}>
            已导出 {ocrExportedTotal}
          </div>
          <div className="stat-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="stat-value" style={{ color: 'var(--c-success)' }}>{ocrOverview ? ocrOverview.qualified.count : 0}</div>
            <div className="stat-label">成功</div>
          </div>
          <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
          <div className="stat-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="stat-value" style={{ color: 'var(--c-accent)' }}>{ocrOverview ? ocrOverview.duplicate.count : 0}</div>
            <div className="stat-label">重复</div>
          </div>
          <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
          <div className="stat-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="stat-value" style={{ color: 'var(--c-danger)' }}>{ocrOverview ? ocrOverview.invalid.count : 0}</div>
            <div className="stat-label">无效</div>
          </div>
          <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
          <div className="stat-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="stat-value" style={{ color: 'var(--c-text-secondary)' }}>{ocrOverview ? ocrOverview.failed.count : 0}</div>
            <div className="stat-label">失败</div>
          </div>
          <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
          {/* 单独一格：四类合计的"待导出"——结构跟左边四格完全一致（大数字+小标签），保证同一行对齐 */}
          <div className="stat-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="stat-value" style={{ color: 'var(--c-primary)' }}>{ocrPendingTotal}</div>
            <div className="stat-label">待导出</div>
          </div>
        </div>


        {/* 批量重新匹配无效：全体范围（管理员=全部；代理=自己+名下），常用于管理员刚导入新数据之后 */}
        {ocrOverview && ocrOverview.invalid.count > 0 && (
          <button
            onClick={handleBulkRematchAll}
            disabled={bulkRematchLoading}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '9px 0', borderRadius: 10, border: '1px solid rgba(255,59,48,0.35)', marginBottom: 10,
              background: bulkRematchLoading ? 'rgba(255,59,48,0.04)' : 'rgba(255,59,48,0.08)',
              color: 'var(--c-danger)', fontSize: 12, fontWeight: 600,
              cursor: bulkRematchLoading ? 'not-allowed' : 'pointer', opacity: bulkRematchLoading ? 0.65 : 1,
            }}
          >
            {bulkRematchLoading ? '匹配中…' : `批量重新匹配无效（${ocrOverview.invalid.count}组）`}
          </button>
        )}

        {/* 待清理导出：常驻展示，"暂不清理"后随时可以回来清理 */}
        {pendingExports.count > 0 && (
          <div className="card" style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
            border: '1px solid rgba(255,149,0,0.3)', background: 'rgba(255,149,0,0.06)'
          }}>
            <AlertTriangle size={20} color="var(--c-accent)" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-accent)' }}>
                有 {pendingExports.count} 批已导出但尚未清理
              </div>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                共 {pendingExports.totalImages} 张图片仍占用服务器空间，确认 ZIP 已保存后可以清理
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={handleCleanupAllPending} disabled={cleaningUp}>
              {cleaningUp ? '清理中...' : '清理已导出图片'}
            </button>
          </div>
        )}

        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : usersWithImages.length === 0 ? (
          <div className="card empty-state">
            <Users size={40} color="var(--c-text-secondary)" strokeWidth={1.2} style={{ margin: '0 auto 12px', display: 'block' }} />
            <p>暂无用户上传图片</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {usersWithImages.map((u, i) => (
              <div
                key={u.id}
                onClick={() => navigate(`/admin/images/${u.id}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '13px 14px',
                  borderBottom: i < usersWithImages.length - 1 ? '0.5px solid var(--c-border)' : 'none',
                  cursor: 'pointer'
                }}
              >
                {/* 头像占位 */}
                <div style={{
                  width: 38, height: 38, borderRadius: 12,
                  background: 'var(--c-primary-light)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <ImageIcon size={18} color="var(--c-primary)" />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--c-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.name || u.username || (String(u.phone || '').startsWith('ext') ? '' : u.phone) || `用户${u.id}`}
                    {u.owner_name && (
                      <span style={{ fontSize: 12, color: 'var(--c-text-secondary)', fontWeight: 400, marginLeft: 4 }}>
                        （归属{u.owner_name}）
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                    {u.username && <span>账号{u.username}</span>}
                    {u.last_upload && (
                      <span style={{ marginLeft: u.username ? 6 : 0 }}>
                        最近: {u.last_upload.slice(5, 16)}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span className="badge badge-success" style={{ fontSize: 13, padding: '4px 10px' }}>
                    {u.image_count} 张
                  </span>
                  {(u.today_image_count || 0) > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--c-warning, #f59e0b)', marginTop: 4 }}>今日 +{u.today_image_count}</div>
                  )}
                  {(u.today_ocr_qualified > 0 || u.today_ocr_duplicate > 0 || u.today_ocr_invalid > 0 || u.today_ocr_failed > 0) && (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 4, flexWrap: 'wrap' }}>
                      {u.today_ocr_qualified > 0 && <span style={{ fontSize: 10, color: 'var(--c-success)', fontWeight: 600, display:'inline-flex', alignItems:'center', gap:2 }}><Check size={9} strokeWidth={2.5}/>{u.today_ocr_qualified}组</span>}
                      {u.today_ocr_duplicate > 0 && <span style={{ fontSize: 10, color: 'var(--c-accent)', fontWeight: 600, display:'inline-flex', alignItems:'center', gap:2 }}><RotateCcw size={9} strokeWidth={2.5}/>{u.today_ocr_duplicate}组</span>}
                      {/* 无效和失败分开展示，不能合并：无效＝识别到号码但不在库/过期，失败＝根本没识别出号码 */}
                      {u.today_ocr_invalid > 0 && <span style={{ fontSize: 10, color: 'var(--c-danger)', fontWeight: 600, display:'inline-flex', alignItems:'center', gap:2 }}><AlertCircle size={9} strokeWidth={2.5}/>{u.today_ocr_invalid}无效</span>}
                      {u.today_ocr_failed > 0 && <span style={{ fontSize: 10, color: 'var(--c-text-secondary)', fontWeight: 600, display:'inline-flex', alignItems:'center', gap:2 }}><XCircle size={9} strokeWidth={2.5}/>{u.today_ocr_failed}失败</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 全体导出确认 */}
      <Modal show={showExportAll} onClose={() => !exporting && setShowExportAll(false)} title="导出全体用户图片">
        <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
          <Download size={44} color="var(--c-primary)" style={{ margin: '0 auto 12px', display: 'block' }} />
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
            导出全部 {totalWithImages} 位用户共 {grandTotal} 张图片？
          </div>
          <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 8, lineHeight: 1.7 }}>
            ZIP 文件结构：<br />
            <code style={{ background: 'var(--c-bg)', padding: '2px 6px', borderRadius: 6, fontSize: 12 }}>
              成员-N张 / 第K批-M张 / 图片（≤200张直接平铺）
            </code>
          </p>
          <p style={{ fontSize: 13, color: 'var(--c-danger)', marginBottom: 20, fontWeight: 600 }}>
            下载后仅标记为"已打包"，不会立即删除，确认完成后可选择立即清理或稍后清理。
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-outline" style={{ flex: 1, height: 46 }}
              onClick={() => setShowExportAll(false)} disabled={exporting}>取消</button>
            <button className="btn btn-primary" style={{ flex: 1, height: 46 }}
              onClick={handleExportAll} disabled={exporting}>
              {exporting
                ? exportTotal > 0
                  ? `打包中...已下载 ${Math.round(exportDownloaded / exportTotal * 100)}%`
                  : exportDownloaded > 0
                  ? `打包中...${(exportDownloaded / 1024 / 1024).toFixed(1)}MB`
                  : <><Loader size={14} /> 打包中...</>
                : <><Download size={14} /> 确认导出</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* 导出后：立即清理 / 暂不清理 */}
      <CleanupPromptModal
        prompt={cleanupPrompt}
        cleaning={cleaningUp}
        onClose={() => setCleanupPrompt(null)}
        onCleanup={async () => {
          if (!cleanupPrompt) return;
          setCleaningUp(true);
          try {
            await api.post(`/images/export/${cleanupPrompt.exportId}/cleanup`);
            showToast('已清理服务器上的已导出图片', 'success');
            fetchSummary();
            fetchPendingExports();
            fetchOcrOverview();
          } catch (err) {
            showToast(err.response?.data?.error || '清理失败', 'error');
          } finally {
            setCleaningUp(false);
            setCleanupPrompt(null);
          }
        }}
      />

      <TabBar mode="admin" />
    </div>
  );
}

// ─── 路由分发：有 userId 参数走用户详情，无则走汇总页 ─────────────────────
export default function ImageManagePage() {
  const { userId } = useParams();
  return userId ? <UserImageView /> : <ImageSummaryPage />;
}
